import { SHIPMENT_STATUS_LABELS, type ShipmentStatus, type ShipmentView } from '@matix/types';
import { shippingLogoFor } from '@matix/ui';
import { useLang } from '../../lib/language';

/** Which tone a status chip takes. */
export function statusTone(status: ShipmentStatus): 'muted' | 'info' | 'active' | 'ok' | 'warn' | 'bad' {
  switch (status) {
    case 'pending':
      return 'muted';
    case 'created':
      return 'info';
    case 'picked_up':
    case 'in_transit':
    case 'out_for_delivery':
      return 'active';
    case 'delivered':
      return 'ok';
    case 'failed_delivery':
    case 'exception':
      return 'warn';
    case 'returned':
    case 'cancelled':
      return 'bad';
  }
}

/** The courier's logo (or its name) beside the normalised status. */
export function ShipmentChip({ shipment, onClick }: { shipment: ShipmentView; onClick?: () => void }): JSX.Element {
  const { lang } = useLang();
  const logo = shippingLogoFor(shipment.provider_code);
  const name = lang === 'ar' ? shipment.provider_name_ar : shipment.provider_name;
  const label = SHIPMENT_STATUS_LABELS[shipment.status][lang];
  const Tag = onClick ? 'button' : 'span';
  return (
    <Tag type={onClick ? 'button' : undefined} className={`ship-chip ship-chip--${statusTone(shipment.status)}`} onClick={onClick} title={shipment.provider_status ?? name}>
      {logo ? <img className="ship-chip-logo" src={logo.src} alt={name} /> : <span className="ship-chip-name">{name}</span>}
      <span>{label}</span>
      {shipment.tracking_number && <span className="ship-chip-track">{shipment.tracking_number}</span>}
    </Tag>
  );
}

/** A courier's logo at option size, with its name for couriers that have none. */
export function CourierMark({ code, name, size = 28 }: { code: string; name: string; size?: number }): JSX.Element {
  const logo = shippingLogoFor(code);
  if (!logo) return <span className="ship-mark ship-mark--text">{name}</span>;
  return (
    <span className="ship-mark" style={{ height: size, background: logo.tile }}>
      <img src={logo.src} alt={name} style={{ maxHeight: size - 8 }} />
    </span>
  );
}
